import { c as _c } from "react/compiler-runtime";
import Link from "@/components/DashboardLink";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { Blocks, FolderKanban, Layers, Slack, SquarePen, Zap, type LucideIcon } from "lucide-react";
import { ShortcutKeys } from "@repo/blacksmith-ui/shortcut-keys";
import { Badge } from "@repo/blacksmith-ui/badge";
import { cn } from "@repo/blacksmith-ui/cn";
import { Skeleton } from "@repo/blacksmith-ui/skeleton";
import { useIsHydrated } from "./skeletonLayout";
import { readCachedAutomationsEnabled } from "./useCachedAutomationsEnabled";
import { readCachedProjectsEnabled } from "./useCachedProjectsEnabled";
import { FOCUS_BORDER_CLASS } from "./focusStyles";

/**
 * Primary navigation above the session lists. The New session row stays
 * pinned under the header; the subpage rows scroll away with the sessions.
 */
export type SidebarPrimaryNavRows = "all" | "new-session" | "subpages";
const NAV_ROW = cn("group flex h-8 w-full items-center gap-2 rounded-md border border-transparent p-1.5 type-body-s-regular cursor-pointer transition-none text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white", FOCUS_BORDER_CLASS);
const NAV_ROW_ACTIVE = "bg-[#0000000f] text-neutral-950 [&_svg]:text-neutral-900 dark:bg-white/6 dark:text-white dark:[&_svg]:text-white";
const NAV_ICON = "size-[18px] shrink-0";

// The "subpages" nav lives inside the session list's scroll container, whose
// wrapper already pads horizontally (px-2) and adds a pt-1 that stands in for
// the gap-1 between New session and the first subpage row.
const NAV_CLASS: Record<SidebarPrimaryNavRows, string> = {
  all: "flex flex-col gap-1 px-2 pt-[calc(var(--spacing-app-header-gap)-0.5rem)] pb-2",
  "new-session": "flex flex-col gap-1 px-2 pt-[calc(var(--spacing-app-header-gap)-0.5rem)]",
  subpages: "flex flex-col gap-1 pb-3"
};

// Split navs need distinct names so landmark navigation can tell them apart.
const NAV_LABEL: Record<SidebarPrimaryNavRows, string | undefined> = {
  all: undefined,
  "new-session": "New session",
  subpages: "Pages"
};

// Paint-only fade under the overlaid keycaps so the label never re-lays out;
// 4.5rem clears all three keycaps.
const LABEL_UNDER_CHIP_FADE_CLASS = "group-hover:[mask-image:linear-gradient(to_left,transparent_4.5rem,black_5.5rem)] group-focus-visible:[mask-image:linear-gradient(to_left,transparent_4.5rem,black_5.5rem)]";
function ShortcutChip(t0) {
  const $ = _c(2);
  const {
    shortcut
  } = t0;
  let t1;
  if ($[0] !== shortcut) {
    t1 = <ShortcutKeys keys={Array.from(shortcut)} className="absolute top-1/2 right-0 -translate-y-1/2 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100" />;
    $[0] = shortcut;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function RowContents(t0) {
  const $ = _c(20);
  const {
    icon: Icon,
    label,
    shortcut,
    alpha: t1
  } = t0;
  const alpha = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== Icon) {
    t2 = <Icon className={NAV_ICON} />;
    $[0] = Icon;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = shortcut && LABEL_UNDER_CHIP_FADE_CLASS;
  let t4;
  if ($[2] !== t3) {
    t4 = cn("flex h-[22px] min-w-0 flex-wrap items-center gap-x-1.5 overflow-hidden", t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== label) {
    t5 = <span className="truncate text-left">{label}</span>;
    $[4] = label;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== alpha) {
    t6 = alpha ? <Badge variant="muted" className="shrink-0">Alpha</Badge> : null;
    $[6] = alpha;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t4 || $[9] !== t5 || $[10] !== t6) {
    t7 = <div className={t4}>{t5}{t6}</div>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== shortcut) {
    t8 = shortcut ? <ShortcutChip shortcut={shortcut} /> : null;
    $[12] = shortcut;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = <div className="relative min-w-0 flex-1">{t7}{t8}</div>;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t2 || $[18] !== t9) {
    t10 = <>{t2}{t9}</>;
    $[17] = t2;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
}
export interface SidebarPrimaryNavProps {
  /** Starts a new Codesmith session (same action as the old toolbar new-chat). */
  onNewSession: () => void;
  /** Resolved automations route, or null/undefined to hide the row (flag off). */
  automationsHref?: string | null;
  onPrefetchAutomations?: () => void;
  /** Resolved projects route, or null/undefined to hide the row (flag off). */
  projectsHref?: string | null;
  /** Resolved MCP Connections route. */
  mcpConnectionsHref: string;
  /** Once the org's Slack workspace has a DM link the row reads "Open in Slack". */
  slackConnected?: boolean;
  /** Holds a placeholder in the Slack row until its connection status resolves. */
  slackLoading?: boolean;
  /**
   * Called after a subpage row navigates (e.g. to collapse the sidebar on
   * mobile). The New session row deliberately does NOT call this: its mobile
   * collapse and write-blocked gating are owned by `onNewSession`, so invoking
   * `onNavigate` there too would double-fire the collapse (and on mobile toggle
   * the sidebar back open).
   */
  onNavigate?: () => void;
  newSessionShortcut?: string;
  automationsShortcut?: string;
  /** Highlights the row matching the current Codesmith subpage. */
  activeRow?: "new-session" | "connect-slack" | "automations" | "projects" | "mcp-connections" | "machine-definitions" | null;
  className?: string;
  /** Which rows this nav renders; the sidebar splits them across two navs. */
  rows?: SidebarPrimaryNavRows;
}
export function SidebarPrimaryNav(t0) {
  const $ = _c(27);
  const {
    onNewSession,
    automationsHref,
    onPrefetchAutomations,
    projectsHref,
    mcpConnectionsHref,
    slackConnected: t1,
    slackLoading: t2,
    onNavigate,
    newSessionShortcut: t3,
    automationsShortcut: t4,
    activeRow: t5,
    className,
    rows: t6
  } = t0;
  const slackConnected = t1 === undefined ? false : t1;
  const slackLoading = t2 === undefined ? false : t2;
  const newSessionShortcut = t3 === undefined ? "\u2325\u21E7N" : t3;
  const automationsShortcut = t4 === undefined ? "\u2325\u21E7A" : t4;
  const activeRow = t5 === undefined ? null : t5;
  const rows = t6 === undefined ? "all" : t6;
  const router = useRouter();
  const org = router.query.org;
  const newSessionHref = typeof org === "string" ? `/${org}/sessions/new` : "/sessions/new";
  const machineDefinitionsHref = typeof org === "string" ? `/${org}/codesmith/machine-definitions` : "/codesmith/machine-definitions";
  const connectSlackHref = typeof org === "string" ? `/${org}/codesmith/connect-slack` : "/codesmith/connect-slack";
  const showNewSession = rows !== "subpages";
  const showSubpages = rows !== "new-session";
  const t7 = NAV_LABEL[rows];
  const t8 = NAV_CLASS[rows];
  let t9;
  if ($[0] !== className || $[1] !== t8) {
    t9 = cn(t8, className);
    $[0] = className;
    $[1] = t8;
    $[2] = t9;
  } else {
    t9 = $[2];
  }
  let t10;
  if ($[3] !== activeRow || $[4] !== newSessionHref || $[5] !== newSessionShortcut || $[6] !== onNewSession || $[7] !== showNewSession) {
    t10 = showNewSession && <Link href={newSessionHref} onClick={e => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      e.preventDefault();
      onNewSession();
    }} aria-keyshortcuts="Alt+Shift+N" aria-current={activeRow === "new-session" ? "page" : undefined} className={cn(NAV_ROW, activeRow === "new-session" && NAV_ROW_ACTIVE)}><RowContents icon={SquarePen} label="New session" shortcut={newSessionShortcut} /></Link>;
    $[3] = activeRow;
    $[4] = newSessionHref;
    $[5] = newSessionShortcut;
    $[6] = onNewSession;
    $[7] = showNewSession;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  let t11;
  if ($[9] !== activeRow || $[10] !== automationsHref || $[11] !== automationsShortcut || $[12] !== connectSlackHref || $[13] !== machineDefinitionsHref || $[14] !== mcpConnectionsHref || $[15] !== onNavigate || $[16] !== onPrefetchAutomations || $[17] !== projectsHref || $[18] !== showSubpages || $[19] !== slackConnected || $[20] !== slackLoading) {
    t11 = showSubpages && <>{slackLoading ? <div className={NAV_ROW} aria-label="Loading Slack connection"><Slack className={NAV_ICON} /><Skeleton className="h-3 w-24 rounded" /></div> : <Link href={connectSlackHref} onClick={onNavigate} aria-current={activeRow === "connect-slack" ? "page" : undefined} className={cn(NAV_ROW, activeRow === "connect-slack" && NAV_ROW_ACTIVE)}><RowContents icon={Slack} label={slackConnected ? "Open in Slack" : "Connect Slack"} /></Link>}{automationsHref ? <Link href={automationsHref} onMouseEnter={onPrefetchAutomations} onFocus={onPrefetchAutomations} onClick={onNavigate} aria-keyshortcuts="Alt+Shift+A" aria-current={activeRow === "automations" ? "page" : undefined} className={cn(NAV_ROW, activeRow === "automations" && NAV_ROW_ACTIVE)}><RowContents icon={Zap} label="Automations" alpha={true} shortcut={automationsShortcut} /></Link> : null}{projectsHref ? <Link href={projectsHref} onClick={onNavigate} aria-current={activeRow === "projects" ? "page" : undefined} className={cn(NAV_ROW, activeRow === "projects" && NAV_ROW_ACTIVE)}><RowContents icon={FolderKanban} label="Projects" alpha={true} /></Link> : null}<Link href={mcpConnectionsHref} onClick={onNavigate} aria-current={activeRow === "mcp-connections" ? "page" : undefined} className={cn(NAV_ROW, activeRow === "mcp-connections" && NAV_ROW_ACTIVE)}><RowContents icon={Blocks} label="MCP Connections" /></Link><Link href={machineDefinitionsHref} onClick={onNavigate} aria-current={activeRow === "machine-definitions" ? "page" : undefined} className={cn(NAV_ROW, activeRow === "machine-definitions" && NAV_ROW_ACTIVE)}><RowContents icon={Layers} label="Machine Images" /></Link></>;
    $[9] = activeRow;
    $[10] = automationsHref;
    $[11] = automationsShortcut;
    $[12] = connectSlackHref;
    $[13] = machineDefinitionsHref;
    $[14] = mcpConnectionsHref;
    $[15] = onNavigate;
    $[16] = onPrefetchAutomations;
    $[17] = projectsHref;
    $[18] = showSubpages;
    $[19] = slackConnected;
    $[20] = slackLoading;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t7 || $[25] !== t9) {
    t12 = <nav aria-label={t7} className={t9}>{t10}{t11}</nav>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t7;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
}

/**
 * Non-interactive twin of SidebarPrimaryNav for the sidebar skeleton: these
 * rows are static chrome, so the skeleton renders them for real instead of
 * shimmer blocks and the top of the sidebar doesn't shift or pop when the
 * live sidebar mounts. The Automations row mirrors the live sidebar's
 * pre-flag fallback by reading the same per-org cache.
 */
export function SidebarPrimaryNavStatic(t0) {
  const $ = _c(20);
  const {
    className,
    rows: t1
  } = t0;
  const rows = t1 === undefined ? "all" : t1;
  const router = useRouter();
  const org = router.query.org;
  const isHydrated = useIsHydrated();
  const orgName = typeof org === "string" ? org : null;
  let t2;
  if ($[0] !== isHydrated || $[1] !== orgName) {
    t2 = isHydrated && readCachedAutomationsEnabled(orgName);
    $[0] = isHydrated;
    $[1] = orgName;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const showAutomations = t2;
  let t3;
  if ($[3] !== isHydrated || $[4] !== orgName) {
    t3 = isHydrated && readCachedProjectsEnabled(orgName);
    $[3] = isHydrated;
    $[4] = orgName;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const showProjects = t3;
  const t4 = NAV_LABEL[rows];
  const t5 = NAV_CLASS[rows];
  let t6;
  if ($[6] !== className || $[7] !== t5) {
    t6 = cn(t5, className);
    $[6] = className;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== rows) {
    t7 = rows !== "subpages" && <div className={NAV_ROW}><RowContents icon={SquarePen} label="New session" /></div>;
    $[9] = rows;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== rows || $[12] !== showAutomations || $[13] !== showProjects) {
    t8 = rows !== "new-session" && <><div className={NAV_ROW} aria-label="Loading Slack connection"><Slack className={NAV_ICON} /><Skeleton className="h-3 w-24 rounded" /></div>{showAutomations && <div className={NAV_ROW}><RowContents icon={Zap} label="Automations" alpha={true} /></div>}{showProjects && <div className={NAV_ROW}><RowContents icon={FolderKanban} label="Projects" alpha={true} /></div>}<div className={NAV_ROW}><RowContents icon={Blocks} label="MCP Connections" /></div><div className={NAV_ROW}><RowContents icon={Layers} label="Machine Images" /></div></>;
    $[11] = rows;
    $[12] = showAutomations;
    $[13] = showProjects;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t4 || $[16] !== t6 || $[17] !== t7 || $[18] !== t8) {
    t9 = <nav aria-label={t4} className={t6}>{t7}{t8}</nav>;
    $[15] = t4;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}